import { useState, type FormEvent } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import { ApiError } from "@/lib/api";

export default function RegisterPage() {
  const [institutionName, setInstitutionName] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirmation, setPasswordConfirmation] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const { register } = useAuth();

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await register(institutionName, name, email, password, passwordConfirmation);
      navigate("/");
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError("Unable to connect to server. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background-100 flex items-center justify-center p-4 sm:p-6 lg:p-10">
      <div className="w-full max-w-6xl bg-white rounded-3xl shadow-xl shadow-foreground-900/5 overflow-hidden lg:grid lg:grid-cols-2">

        {/* ── Left: Form Panel ─────────────────────────────────────── */}
        <div className="flex flex-col p-8 sm:p-12 lg:p-14">
          <img src="/logo.svg" alt="SupplyGuard" className="h-[82px] w-auto self-center mb-8 sm:mb-10" />

          <div className="w-full max-w-sm mx-auto lg:mx-0 flex-1 flex flex-col justify-center">
            <h2 className="text-foreground-900 text-3xl font-bold mb-2">Create your account</h2>
            <p className="text-foreground-500 text-sm mb-6">Set up SupplyGuard for your organisation.</p>

            <form onSubmit={handleSubmit} className="space-y-3.5">

              {/* Institution name */}
              <div>
                <label htmlFor="institution_name" className="block text-foreground-700 text-sm font-medium mb-2">
                  Business / Institution name
                </label>
                <input
                  id="institution_name"
                  type="text"
                  value={institutionName}
                  onChange={(e) => setInstitutionName(e.target.value)}
                  placeholder="e.g. Acme Municipality"
                  required
                  className="w-full h-12 px-4 bg-white border border-background-300 rounded-xl text-sm text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/15 transition-all"
                />
              </div>

              {/* Name */}
              <div>
                <label htmlFor="name" className="block text-foreground-700 text-sm font-medium mb-2">
                  Your full name
                </label>
                <input
                  id="name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Jane Mokoena"
                  required
                  className="w-full h-12 px-4 bg-white border border-background-300 rounded-xl text-sm text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/15 transition-all"
                />
              </div>

              {/* Email */}
              <div>
                <label htmlFor="email" className="block text-foreground-700 text-sm font-medium mb-2">
                  Email address
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="name@department.gov.za"
                  required
                  className="w-full h-12 px-4 bg-white border border-background-300 rounded-xl text-sm text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/15 transition-all"
                />
              </div>

              {/* Password */}
              <div>
                <label htmlFor="password" className="block text-foreground-700 text-sm font-medium mb-2">
                  Password
                </label>
                <div className="relative">
                  <input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="min 8 chars"
                    required
                    minLength={8}
                    className="w-full h-12 px-4 pr-11 bg-white border border-background-300 rounded-xl text-sm text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/15 transition-all"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute right-3.5 top-1/2 -translate-y-1/2 text-foreground-400 hover:text-foreground-600 transition-colors p-1 cursor-pointer"
                    tabIndex={-1}
                  >
                    <i className={`${showPassword ? "ri-eye-off-line" : "ri-eye-line"} text-sm`}></i>
                  </button>
                </div>
              </div>

              {/* Confirm password */}
              <div>
                <label htmlFor="password_confirmation" className="block text-foreground-700 text-sm font-medium mb-2">
                  Confirm password
                </label>
                <input
                  id="password_confirmation"
                  type={showPassword ? "text" : "password"}
                  value={passwordConfirmation}
                  onChange={(e) => setPasswordConfirmation(e.target.value)}
                  placeholder="Re-enter your password"
                  required
                  minLength={8}
                  className="w-full h-12 px-4 bg-white border border-background-300 rounded-xl text-sm text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/15 transition-all"
                />
              </div>

              {/* Error */}
              {error && (
                <div className="flex items-start gap-2.5 bg-accent-50 border border-accent-200 rounded-lg px-3.5 py-3">
                  <i className="ri-error-warning-line text-accent-600 text-sm mt-0.5 shrink-0"></i>
                  <p className="text-accent-700 text-sm leading-snug">{error}</p>
                </div>
              )}

              {/* Submit */}
              <button
                type="submit"
                disabled={loading}
                className="w-full h-12 bg-primary-600 hover:bg-primary-700 active:bg-primary-800 text-white text-sm font-semibold rounded-xl transition-all disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2 mt-2"
              >
                {loading ? (
                  <>
                    <i className="ri-loader-4-line animate-spin text-sm"></i>
                    <span>Creating account…</span>
                  </>
                ) : (
                  <span>Create account</span>
                )}
              </button>
            </form>

            <p className="text-foreground-400 text-xs text-center mt-6 leading-relaxed">
              Already have an account?{" "}
              <Link to="/login" className="text-primary-600 font-medium hover:text-primary-700">
                Sign in
              </Link>
            </p>
          </div>

          <p className="text-foreground-400 text-xs mt-10">
            © {new Date().getFullYear()} SupplyGuard, All Rights Reserved.
          </p>
        </div>

        {/* ── Right: Brand Panel ────────────────────────────────────── */}
        <div className="hidden lg:flex flex-col relative overflow-hidden bg-primary-600 p-12">
          <div className="absolute -top-24 -right-24 w-80 h-80 rounded-full bg-white/10 blur-3xl pointer-events-none" />
          <div className="absolute bottom-0 -left-16 w-64 h-64 rounded-full bg-primary-900/30 blur-3xl pointer-events-none" />

          <div className="relative z-10">
            <h1 className="text-white text-3xl font-bold leading-tight mb-3 max-w-sm">
              Bring your whole organisation's contracts into one place.
            </h1>
            <p className="text-primary-200 text-sm leading-relaxed max-w-xs">
              Your own private workspace — contracts, suppliers, renewals, and audit trail, isolated from every other SupplyGuard customer.
            </p>
          </div>

          <div className="relative z-10 mt-10 space-y-4">
            {[
              { icon: "ri-shield-check-line", text: "Your data is never visible to other organisations" },
              { icon: "ri-user-add-line", text: "You'll be the first admin for your institution" },
              { icon: "ri-flow-chart", text: "Default renewal workflow set up automatically" },
            ].map((item) => (
              <div key={item.text} className="flex items-center gap-3 bg-white/10 rounded-xl p-4">
                <div className="w-9 h-9 rounded-lg bg-white/15 flex items-center justify-center shrink-0">
                  <i className={`${item.icon} text-white text-base`}></i>
                </div>
                <p className="text-primary-100 text-sm">{item.text}</p>
              </div>
            ))}
          </div>
        </div>

      </div>
    </div>
  );
}
